import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Tabs,
  Tab,
  Grid,
  CircularProgress,
  Alert,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Radio,
  RadioGroup,
  Checkbox,
  FormGroup,
  Button,
  Input,
  Snackbar,
} from '@mui/material';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import API_ENDPOINTS from '../../config/api';

export default function ThemeSettings() {
  const navigate = useNavigate();
  const location = useLocation(); // Access navigation state
  const [types, setTypes] = useState([]);
  const [settings, setSettings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tabIndex, setTabIndex] = useState(0);
  const [formData, setFormData] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  useEffect(() => {
    const fetchApplicationTypes = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        if (!token) {
          navigate('/login');
          return;
        }

        const response = await axios.get(API_ENDPOINTS.APPLICATION_TYPES.BASE, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        const typesData = response.data.data || [];
        setTypes(typesData);

        // Handle navigation state to set the appropriate tab
        if (location.state?.fromCustomerOrders === true) {
          const customerOrdersIndex = typesData.findIndex(
            (type) => type.type.toLowerCase() === 'customer orders'
          );
          if (customerOrdersIndex !== -1) {
            setTabIndex(customerOrdersIndex);
          } else {
            console.warn('Customer Orders tab not found in types');
            setTabIndex(0); // Fallback to first tab
          }
        } else if (location.state?.fromCustomerOrders === false) {
          setTabIndex(0); // Explicitly set to first tab
        }
        // If no state is provided, tabIndex remains 0 (default)
      } catch (error) {
        console.error('Error fetching application types:', error);
        handleShowSnackbar('Failed to fetch application types. ' + (error.response?.data?.message || ''), 'error');
      } finally {
        setLoading(false);
      }
    };

    const fetchApplicationSettings = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        if (!token) {
          navigate('/login');
          return;
        }

        const response = await axios.get(API_ENDPOINTS.APPLICATION_SETTINGS.BASE, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        const settingsData = response.data.data || [];
        setSettings(settingsData);

        const initialFormData = {};
        settingsData.forEach((setting) => {
          initialFormData[setting.id] = {
            value: setting.value || '',
            file: null,
            alt_text: setting.alt_text || '',
          };
        });
        setFormData(initialFormData);
      } catch (error) {
        console.error('Error fetching application settings:', error);
        handleShowSnackbar('Failed to fetch application settings. ' + (error.response?.data?.message || ''), 'error');
      } finally {
        setLoading(false);
      }
    };

    fetchApplicationTypes();
    fetchApplicationSettings();
  }, [navigate, location.state]);

  useEffect(() => {
    document.body.classList.add("main-theme-settings");
    return () => {
      document.body.classList.remove("main-theme-settings");
    };
  }, []);

  const handleTabChange = (event, newValue) => {
    setTabIndex(newValue);
  };

  const handleInputChange = (settingId, value, isFile = false, altText = null) => {
    setFormData((prev) => ({
      ...prev,
      [settingId]: {
        ...prev[settingId],
        value: isFile ? value : value,
        file: isFile ? value : null,
        alt_text: altText !== null ? altText : prev[settingId].alt_text,
      },
    }));
  };

  const fileToBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });

  const handleShowSnackbar = (message, severity) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setOpenSnackbar(true);
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpenSnackbar(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      const currentType = types[tabIndex];
      if (!currentType) {
        handleShowSnackbar('No application type selected.', 'error');
        setSubmitting(false);
        return;
      }

      const settingsPayload = await Promise.all(
        filteredSettings.map(async (setting) => {
          const settingData = formData[setting.id] || {};
          let value = settingData.value;

          if (setting.input_type === 'file' && settingData.file instanceof File) {
            value = await fileToBase64(settingData.file);
          } else if (setting.input_type === 'multiple-files' && Array.isArray(settingData.value)) {
            const base64Files = await Promise.all(
              settingData.value
                .filter((file) => file instanceof File)
                .map((file) => fileToBase64(file))
            );
            value = [
              ...(settingData.value.filter((file) => typeof file === 'string')),
              ...base64Files,
            ];
          } else if (setting.input_type === 'checkbox') {
            value = settingData.value ? settingData.value.split(',') : [];
          } else if (setting.input_type === 'switch') {
            value = settingData.value === '1' || settingData.value === true ? 1 : 0;
          }

          return {
            id: setting.id,
            value: value,
            alt_text: settingData.alt_text || '',
          };
        })
      );

      const response = await axios.post(
        '/api/application-settings/update-by-type',
        {
          setting_type_id: currentType.id,
          setting_type_slug: currentType.slug,
          settings: settingsPayload,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      const settingsResponse = await axios.get(API_ENDPOINTS.APPLICATION_SETTINGS.BASE, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const settingsData = settingsResponse.data.data || [];
      setSettings(settingsData);

      const updatedFormData = {};
      settingsData.forEach((setting) => {
        updatedFormData[setting.id] = {
          value: setting.value || '',
          file: null,
          alt_text: setting.alt_text || '',
        };
      });
      setFormData(updatedFormData);

      handleShowSnackbar('Theme settings updated successfully!', 'success');
    } catch (error) {
      console.error('Error updating theme settings:', error);
      let errorMessage = 'Failed to update theme settings: ' + (error.response?.data?.message || 'Unknown error');
      if (error.response?.status === 422) {
        const validationErrors = error.response.data.errors;
        errorMessage += '\n' + Object.values(validationErrors).flat().join('\n');
      }
      handleShowSnackbar(errorMessage, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const renderInputField = (setting) => {
    switch (setting.input_type) {
      case 'heading':
        return (
          <Typography variant="h6" gutterBottom>
            {setting.field_name}
          </Typography>
        );

      case 'color':
        return (
          <Grid container spacing={2} sx={{ marginBottom: '16px' }}>
            <Grid item xs={12}>
              <InputLabel sx={{ fontSize: '1.2rem', fontWeight: 'bold' }}>
                {setting.field_name}
              </InputLabel>
            </Grid>
            <Grid item xs={12} md={6} sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <FormControl sx={{ width: 'auto' }}>
                <Input
                  type="color"
                  value={formData[setting.id]?.value || '#000000'}
                  onChange={(e) => handleInputChange(setting.id, e.target.value)}
                  sx={{
                    width: '40px',
                    height: '40px',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    padding: '0',
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'pointer',
                  }}
                />
              </FormControl>
              <FormControl sx={{ flex: 1 }}>
                <Input
                  type="text"
                  value={formData[setting.id]?.value || '#000000'}
                  onChange={(e) => handleInputChange(setting.id, e.target.value)}
                  sx={{
                    width: '100%',
                    height: '40px',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    padding: '8px',
                  }}
                />
              </FormControl>
            </Grid>
          </Grid>
        );

      case 'textbox':
      case 'number':
        return (
          <FormControl fullWidth margin="normal">
            <TextField
              label={setting.field_name}
              value={formData[setting.id]?.value || ''}
              onChange={(e) => {
                let value = e.target.value;
                if (setting.input_type === 'number') {
                  value = value.replace(/\D/g, '');
                }
                handleInputChange(setting.id, value);
              }}
              type={setting.input_type === 'number' ? 'number' : 'text'}
            />
          </FormControl>
        );

      case 'select':
        const options = setting.options ? setting.options.split(',').map((opt) => opt.trim()) : [];
        return (
          <FormControl fullWidth margin="normal">
            <InputLabel>{setting.field_name}</InputLabel>
            <Select
              value={formData[setting.id]?.value || ''}
              onChange={(e) => handleInputChange(setting.id, e.target.value)}
              label={setting.field_name}
            >
              {options.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );

      case 'radio':
        const radioOptions = setting.options ? setting.options.split(',').map((opt) => opt.trim()) : [];
        return (
          <FormControl component="fieldset" margin="normal">
            <Typography variant="subtitle1">{setting.field_name}</Typography>
            <RadioGroup
              value={formData[setting.id]?.value || ''}
              onChange={(e) => handleInputChange(setting.id, e.target.value)}
            >
              {radioOptions.map((option) => (
                <FormControlLabel
                  key={option}
                  value={option}
                  control={<Radio />}
                  label={option}
                />
              ))}
            </RadioGroup>
          </FormControl>
        );

      case 'checkbox':
        const checkboxOptions = setting.options ? setting.options.split(',').map((opt) => opt.trim()) : [];
        const checkedValues = formData[setting.id]?.value ? formData[setting.id].value.split(',') : [];
        return (
          <FormControl component="fieldset" margin="normal">
            <Typography variant="subtitle1">{setting.field_name}</Typography>
            <FormGroup>
              {checkboxOptions.map((option) => (
                <FormControlLabel
                  key={option}
                  control={
                    <Checkbox
                      checked={checkedValues.includes(option)}
                      onChange={(e) => {
                        const newValues = e.target.checked
                          ? [...checkedValues, option]
                          : checkedValues.filter((val) => val !== option);
                        handleInputChange(setting.id, newValues.join(','));
                      }}
                    />
                  }
                  label={option}
                />
              ))}
            </FormGroup>
          </FormControl>
        );

      case 'textarea':
      case 'textarea-normal':
        return (
          <TextField
            fullWidth
            label={setting.field_name}
            multiline
            rows={4}
            value={formData[setting.id]?.value || ''}
            onChange={(e) => handleInputChange(setting.id, e.target.value)}
            margin="normal"
          />
        );

      case 'file':
        return (
          <FormControl fullWidth margin="normal">
            <Typography variant="subtitle1">{setting.field_name}</Typography>
            <Input
              type="file"
              onChange={(e) => handleInputChange(setting.id, e.target.files[0], true)}
            />
            <TextField
              fullWidth
              label="Alt Text"
              value={formData[setting.id]?.alt_text || ''}
              onChange={(e) => handleInputChange(setting.id, formData[setting.id].value, true, e.target.value)}
              margin="normal"
            />
            {formData[setting.id]?.value && typeof formData[setting.id].value === 'string' && (
              <Box mt={1}>
                <img
                  src={formData[setting.id].value}
                  alt={formData[setting.id].alt_text || setting.field_name}
                  style={{ height: '50px', width: '50px' }}
                />
              </Box>
            )}
          </FormControl>
        );

      case 'multiple-files':
        return (
          <FormControl fullWidth margin="normal">
            <Typography variant="subtitle1">{setting.field_name}</Typography>
            <Input
              type="file"
              inputProps={{ multiple: true }}
              onChange={(e) => handleInputChange(setting.id, Array.from(e.target.files), true)}
            />
            <TextField
              fullWidth
              label="Alt Text"
              value={formData[setting.id]?.alt_text || ''}
              onChange={(e) => handleInputChange(setting.id, formData[setting.id].value, true, e.target.value)}
              margin="normal"
            />
            {Array.isArray(formData[setting.id]?.value) && (
              <Box mt={1} display="flex" gap={1}>
                {formData[setting.id].value.map((file, index) =>
                  typeof file === 'string' ? (
                    <img
                      key={index}
                      src={file}
                      alt={formData[setting.id].alt_text || `${setting.field_name} ${index + 1}`}
                      style={{ maxWidth: '100px' }}
                    />
                  ) : null
                )}
              </Box>
            )}
          </FormControl>
        );

      case 'switch':
        return (
          <FormControl fullWidth margin="normal">
            <FormControlLabel
              control={
                <Switch
                  checked={formData[setting.id]?.value === '1'}
                  onChange={(e) => handleInputChange(setting.id, e.target.checked ? '1' : '0')}
                />
              }
              label={setting.field_name}
            />
          </FormControl>
        );

      default:
        return null;
    }
  };

  const filteredSettings = types[tabIndex]?.id
    ? settings.filter((setting) => setting.application_setting_type_id === types[tabIndex].id)
    : [];

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Paper sx={{ p: 3 }}>
          {loading ? (
            <Box display="flex" justifyContent="center" my={4}>
              <CircularProgress />
            </Box>
          ) : null}

          <Tabs value={tabIndex} onChange={handleTabChange} variant="scrollable" scrollButtons="auto">
            {types.map((type, index) => (
              <Tab label={type.type} key={index} />
            ))}
          </Tabs>

          <Box mt={3}>
            {types[tabIndex] ? (
              <form onSubmit={handleSubmit}>
                <Typography variant="h6" gutterBottom>
                  Type: <strong>{types[tabIndex].type}</strong>
                </Typography>

                {filteredSettings.length > 0 ? (
                  <Grid container spacing={2}>
                    {filteredSettings.map((setting, idx) => (
                      <Grid item xs={12} md={setting.input_type === 'textbox' || setting.input_type === 'number' ? 6 : 12} key={idx}>
                        {renderInputField(setting)}
                      </Grid>
                    ))}
                    <Grid item xs={12}>
                      <Box mt={3}>
                        <Button
                          variant="contained"
                          color="primary"
                          type="submit"
                          disabled={submitting}
                        >
                          {submitting ? 'Saving...' : 'Save Settings'}
                        </Button>
                      </Box>
                    </Grid>
                  </Grid>
                ) : (
                  <Typography variant="body2">No settings available for this type.</Typography>
                )}
              </form>
            ) : (
              <Typography>No data available</Typography>
            )}
          </Box>
        </Paper>
      </Grid>
      <Snackbar open={openSnackbar} autoHideDuration={6000} onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Grid>
  );
}